import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { useComposableFilter } from '@/components/ComposableFilter';
import type { NormalizedDateFilter } from '@/components/DatePicker/RichDatePicker';
import { useFormatDate, useFormatDistance } from '@/hooks/date';
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter';
import { usePaginationSearch, useSortingSearch } from '@/lib/listPage';

import {
  ContractDealRow,
  ContractDealTable,
} from './-components/ContractDealTable';
import { ContractSearchFilterBar } from './-components/ContractSearchFilterBar';
import { useContractDealList } from './-query';

/** テーブルカラムIDからAPIのorderByに変換 */
function toOrderBy(
  sortColumn: string | undefined,
):
  | 'deviceCatalogName'
  | 'contractTitle'
  | 'contractStatus'
  | 'contractPeriod'
  | 'leasePeriod'
  | 'responsiblePerson'
  | undefined {
  if (!sortColumn) return undefined;
  return match(sortColumn)
    .with('item', () => 'deviceCatalogName' as const)
    .with('contract', () => 'contractTitle' as const)
    .with('status', () => 'contractStatus' as const)
    .with('contractPeriod', () => 'contractPeriod' as const)
    .with('leasePeriod', () => 'leasePeriod' as const)
    .with('responsiblePerson', () => 'responsiblePerson' as const)
    .otherwise(() => undefined);
}

const SearchSchema = z.object({
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  freeText: z.string().optional(),
  showTerminated: z.boolean().default(true),
  contractStartFrom: z.string().optional(),
  contractStartTo: z.string().optional(),
  contractEndFrom: z.string().optional(),
  contractEndTo: z.string().optional(),
  sortColumn: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export const Route = createFileRoute(
  '/_staff/devices/_list/contract-and-cost/_contract-and-cost/deal/_deal/contract/',
)({
  component: ContractTab,
  validateSearch: zodValidator(SearchSchema),
});

type EmptyFilter = Record<string, never>;

function ContractTab() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filter = useComposableFilter<EmptyFilter>({
    filterDefs: [],
    filterState: {} as EmptyFilter,
    onFilterStateChange: () => {},
  });

  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: (s) => navigate({ search: s }),
  });

  const [sorting, setSorting] = useSortingSearch({
    search,
    onSearchChange: (s) => navigate({ search: s }),
  });

  const orderByParam = useMemo(
    () => toOrderBy(search.sortColumn),
    [search.sortColumn],
  );

  const { data, isPending } = useContractDealList({
    ...search,
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    includeTerminated: search.showTerminated,
    orderBy: orderByParam,
    orderDirection: search.sortOrder,
  });

  const formatDate = useFormatDate();
  const formatDistance = useFormatDistance();
  const rows = useMemo(() => {
    return (
      data?.items.map<ContractDealRow>((item) => {
        return {
          item: {
            id: item.deviceCatalogId,
            name: item.deviceCatalogName,
            genre: item.deviceCatalogGenre ?? 'OTHER',
            avatarUrl: item.deviceCatalogAvatarUrl ?? undefined,
          },
          contract: {
            id: item.contractId,
            name: item.contractTitle,
            contractType: item.contractTypeName,
          },
          status: {
            status: item.contractStatus,
            statusLabel: item.contractStatusName,
          },
          contractPeriod: {
            startDate: item.contractStartDate
              ? formatDate(item.contractStartDate)
              : '',
            endDate: item.contractEndDate
              ? formatDate(item.contractEndDate)
              : '',
            duration:
              item.contractStartDate && item.contractEndDate
                ? formatDistance(item.contractStartDate, item.contractEndDate, {
                    isPeriodDistance: true,
                  })
                : '',
          },
          leasePeriod: {
            startDate: item.leaseStartDate
              ? formatDate(item.leaseStartDate)
              : '',
            endDate: item.leaseEndDate ? formatDate(item.leaseEndDate) : '',
            duration:
              item.leaseStartDate && item.leaseEndDate
                ? formatDistance(item.leaseStartDate, item.leaseEndDate, {
                    isPeriodDistance: true,
                  })
                : '',
          },
          responsiblePerson: item.responsiblePerson
            ? {
                id: item.responsiblePerson.id,
                name: item.responsiblePerson.name,
                avatarUrl: item.responsiblePerson.avatarUrl ?? undefined,
              }
            : null,
          evidence:
            item.evidenceFiles.length > 0
              ? {
                  label: item.evidenceFiles[0].originalName,
                  additionalCount: item.evidenceFiles.length - 1,
                }
              : null,
        };
      }) ?? []
    );
  }, [data, formatDate, formatDistance]);

  const {
    currentFilter: contractStartDateFilter,
    handleFilterChange: handleContractStartQueryChange,
  } = useDateRangeFilter(search.contractStartFrom, search.contractStartTo);

  const handleContractStartDateFilterChange = useCallback(
    ({ normalized }: { normalized?: NormalizedDateFilter }) => {
      const { fromDate, toDate } = handleContractStartQueryChange(normalized);
      navigate({
        search: (prev) => ({
          ...prev,
          contractStartFrom: fromDate,
          contractStartTo: toDate,
          pageIndex: 0,
        }),
      });
    },
    [navigate, handleContractStartQueryChange],
  );

  const {
    currentFilter: contractEndDateFilter,
    handleFilterChange: handleContractEndQueryChange,
  } = useDateRangeFilter(search.contractEndFrom, search.contractEndTo);

  const handleContractEndDateFilterChange = useCallback(
    ({ normalized }: { normalized?: NormalizedDateFilter }) => {
      const { fromDate, toDate } = handleContractEndQueryChange(normalized);
      navigate({
        search: (prev) => ({
          ...prev,
          contractEndFrom: fromDate,
          contractEndTo: toDate,
          pageIndex: 0,
        }),
      });
    },
    [navigate, handleContractEndQueryChange],
  );

  const handleShowTerminatedChange = useCallback(
    (checked: boolean) => {
      navigate({
        search: (prev) => ({
          ...prev,
          showTerminated: checked,
          pageIndex: 0,
        }),
      });
    },
    [navigate],
  );

  return (
    <>
      <ContractSearchFilterBar
        searchValue={search.freeText ?? ''}
        onSearchChange={(e) =>
          navigate({
            search: (prev) => ({
              ...prev,
              freeText: e.target.value || undefined,
              pageIndex: 0,
            }),
          })
        }
        filter={filter}
        contractStartDateFilter={contractStartDateFilter}
        onContractStartDateFilterChange={handleContractStartDateFilterChange}
        contractEndDateFilter={contractEndDateFilter}
        onContractEndDateFilterChange={handleContractEndDateFilterChange}
        showTerminated={search.showTerminated}
        onShowTerminatedChange={handleShowTerminatedChange}
      />
      <ContractDealTable
        data={rows ?? []}
        isPending={isPending}
        totalCount={data?.count}
        pageCount={data?.maxPage}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        className="flex-1 min-h-0"
      />
    </>
  );
}
